import type { MatchState, PlayerState, RefereeState, DisciplineState, AdvantageState, PendingCard, BallState } from '../sim/state';
import type { ActionKind, MatchPhase, RestartKind, PitchCondition, CardKind, TeamIdx } from '../types';
import { fnv1a64 } from '../math/hash';
import { ProtocolError } from '../errors';

// Canonical, exact (raw f64) binary encoding of MatchState (§9.4). Deterministic field order; every
// numeric field is written as f64 so a 1e-9 perturbation changes the bytes (⇒ the hash). Feeds
// hashState + keyframes. decodeState (round-trip) is added in M2 (T-PROTO-001).

const PHASES: MatchPhase[] = ['LOBBY', 'KICKOFF', 'PLAY', 'RESTART_PENDING', 'GOAL_CELEBRATION', 'HALFTIME', 'FULLTIME'];
/** **WIRE ORDER — append only, never reorder:** the index IS the encoding, here and in `protocol/events`.
 *  Exported so a validator (D141 parses a restart off the Welcome JSON) cannot drift from the codec. */
export const RESTART_KINDS: readonly RestartKind[] = ['KICKOFF', 'THROW_IN', 'KICK_IN', 'CORNER', 'GOAL_KICK', 'FREE_KICK', 'PENALTY', 'DROP_BALL'];
const RESTARTS = RESTART_KINDS;
export const ACTIONS: ActionKind[] = [
  'IDLE', 'RUN', 'KICK_WINDUP', 'KICK_RELEASE', 'TACKLE_STAND', 'TACKLE_SLIDE', 'SLIDE_RECOVERY', 'SHOVE',
  'PUNCH_WINDUP', 'PUNCH_ACTIVE', 'PUNCH_RECOVERY', 'DIVE_FALL', 'PRONE', 'GETUP', 'RAGDOLL', 'SHIELD',
  'GK_DIVE', 'GK_HOLD', 'HEADER_JUMP', 'CELEBRATE',
  'GK_SMOTHER',
  'TRAP', // D45 Item 6 — APPEND ONLY (index-stable; existing actions keep their codes)
  'BLOCK', // D82 — APPEND ONLY (new body-block stance; protocol 6→7 so old clients are refused)
];
const CONDITIONS: PitchCondition[] = ['DRY', 'RAIN', 'MUD'];
const REASONS = ['FOUL', 'SIMULATION', 'VIOLENT', 'SECOND_YELLOW', 'REF_ABUSE'] as const;
const cardIdx = (c: CardKind | null): number => (c === null ? 0 : c === 'YELLOW' ? 1 : 2);

class Writer {
  private view = new DataView(new ArrayBuffer(1 << 16));
  private off = 0;
  f(v: number): void {
    this.view.setFloat64(this.off, v, true);
    this.off += 8;
  }
  u32(v: number): void {
    this.view.setUint32(this.off, v >>> 0, true);
    this.off += 4;
  }
  i32(v: number): void {
    this.view.setInt32(this.off, v | 0, true);
    this.off += 4;
  }
  u8(v: number): void {
    this.view.setUint8(this.off, v & 0xff);
    this.off += 1;
  }
  bytes(): Uint8Array {
    return new Uint8Array(this.view.buffer, 0, this.off);
  }
}

function writePlayer(w: Writer, p: PlayerState): void {
  w.u8(p.id);
  w.u8(p.team);
  w.f(p.pos.x);
  w.f(p.pos.y);
  w.f(p.vel.x);
  w.f(p.vel.y);
  w.f(p.facing);
  w.f(p.z);
  w.f(p.vz);
  w.f(p.stamina);
  w.u8(ACTIONS.indexOf(p.action));
  w.i32(p.actionTicks);
  if (p.charge) {
    w.u8(1);
    w.u32(p.charge.button);
    w.i32(p.charge.ticks);
  } else {
    w.u8(0);
  }
  w.u8(p.sprinting ? 1 : 0);
  w.u32(p.prevButtons);
  w.u8(p.shouting ? 1 : 0);
  w.u8(p.calling ? 1 : 0); // D42
  w.u8(p.offsideLiable ? 1 : 0);
  w.u32(p.intentBits >>> 0); // D62 one-touch intent latch
  w.i32(p.intentTick);
  w.u8(cardIdx(p.card));
  w.u8(p.sentOff ? 1 : 0);
  w.u32(p.suspendedUntilTick >>> 0); // D65c: red-card return deadline (keyframe codec + hash)
  w.u8(p.isGk ? 1 : 0);
  w.u8(p.punchHit ? 1 : 0);
  if (p.lastContact) {
    w.u8(1);
    w.u8(p.lastContact.by);
    w.u32(p.lastContact.tick);
    w.f(p.lastContact.impulse.x);
    w.f(p.lastContact.impulse.y);
  } else {
    w.u8(0);
  }
}

/** Canonical binary encoding of the full MatchState (§9.4). */
export function encodeState(s: MatchState): Uint8Array {
  const w = new Writer();
  w.u32(s.tick);
  w.u32(s.rng);
  w.u8(PHASES.indexOf(s.phase));
  w.i32(s.phaseTicksLeft);
  w.u8(s.half);
  w.i32(s.clockTicks);
  w.i32(s.attackDir[0]);
  w.i32(s.attackDir[1]);
  w.u32(s.score[0]);
  w.u32(s.score[1]);
  w.u8(CONDITIONS.indexOf(s.conditions));
  // mode
  w.u8(s.mode.teamSize);
  w.u8(s.mode.offside ? 1 : 0);
  w.u32(s.mode.halfLengthTicks);
  w.u8(s.mode.overtime === 'GOLDEN_GOAL' ? 1 : 0);
  w.u8(CONDITIONS.indexOf(s.mode.conditions));
  // restart
  if (s.restart) {
    w.u8(1);
    w.u8(RESTARTS.indexOf(s.restart.kind));
    w.f(s.restart.spot.x);
    w.f(s.restart.spot.y);
    w.u8(s.restart.team);
    w.u8(s.restart.taker === null ? 255 : s.restart.taker); // D38: designated taker (255 = none)
  } else {
    w.u8(0);
  }
  // celebration (D108) — hashed, so a desynced celebrant draw is caught by T-LOG-002 like any other
  if (s.celebration) {
    w.u8(1);
    w.u8(s.celebration.scorer === null ? 255 : s.celebration.scorer); // 255 = own goal, no scorer
    w.u8(s.celebration.team);
    w.u8(s.celebration.celebrants.length);
    for (const id of s.celebration.celebrants) w.u8(id);
  } else {
    w.u8(0);
  }
  // players
  w.u32(s.players.length);
  for (const p of s.players) writePlayer(w, p);
  // ball
  const b = s.ball;
  w.f(b.pos.x);
  w.f(b.pos.y);
  w.f(b.pos.z);
  w.f(b.vel.x);
  w.f(b.vel.y);
  w.f(b.vel.z);
  w.f(b.spin.x);
  w.f(b.spin.y);
  w.f(b.spin.z);
  w.i32(b.possessor ?? -1);
  w.i32(b.gkLock ?? -1); // D18 re-handle lock
  w.i32(b.claimCooldownTicks);
  w.u8(b.claimCooldownOnly === null ? 255 : b.claimCooldownOnly); // D43
  w.u8(b.kickedBy === null ? 255 : b.kickedBy); // D43
  w.i32(b.trappedTicks);
  w.u8(b.shotPending ? 1 : 0);
  if (b.lastTouch) {
    w.u8(1);
    w.u8(b.lastTouch.entity);
    w.u8(b.lastTouch.team);
    w.u32(b.lastTouch.tick);
  } else {
    w.u8(0);
  }
  // referee
  const r = s.referee;
  w.f(r.pos.x);
  w.f(r.pos.y);
  w.f(r.vel.x);
  w.f(r.vel.y);
  w.f(r.viewDir);
  w.f(r.aiTargetPos.x);
  w.f(r.aiTargetPos.y);
  w.i32(r.scanTicksLeft);
  // discipline
  w.u32(s.discipline.perPlayer.length);
  for (const d of s.discipline.perPlayer) {
    w.u8(d.yellows);
    w.u8(d.red ? 1 : 0);
    w.f(d.diveReputation);
    w.i32(d.foulsCommitted);
    w.i32(d.foulsSuffered);
    w.i32(d.punchesThrown);
    w.i32(d.divesAttempted);
    w.i32(d.divesWon);
    w.i32(d.divesCaught);
  }
  // advantage
  if (s.advantage) {
    const a = s.advantage;
    w.u8(1);
    w.u8(a.offender);
    w.u8(a.victim);
    w.u8(a.victimTeam);
    w.f(a.severity);
    w.i32(a.ticksLeft);
    w.u8(RESTARTS.indexOf(a.restart.kind));
    w.f(a.restart.spot.x);
    w.f(a.restart.spot.y);
    w.u8(a.restart.team);
    w.u8(a.restart.taker === null ? 255 : a.restart.taker);
  } else {
    w.u8(0);
  }
  // cardQueue
  w.u32(s.cardQueue.length);
  for (const c of s.cardQueue) {
    w.u8(c.entity);
    w.u8(cardIdx(c.card));
    w.u8(REASONS.indexOf(c.reason));
  }
  return w.bytes();
}

/** Canonical state hash (§4.3): FNV-1a 64 over the canonical encoding. */
export function hashState(s: MatchState): string {
  return fnv1a64(encodeState(s));
}

// ─────────────────────────── decodeState (round-trip; §9.4 T-PROTO-001) ───────────────────────────

class Reader {
  private off = 0;
  constructor(
    private readonly view: DataView,
    private readonly len: number,
  ) {}
  private need(n: number): void {
    if (this.off + n > this.len) throw new ProtocolError('decodeState: unexpected end of buffer');
  }
  f(): number {
    this.need(8);
    const v = this.view.getFloat64(this.off, true);
    this.off += 8;
    return v;
  }
  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  i32(): number {
    this.need(4);
    const v = this.view.getInt32(this.off, true);
    this.off += 4;
    return v;
  }
  u8(): number {
    this.need(1);
    const v = this.view.getUint8(this.off);
    this.off += 1;
    return v;
  }
  get atEnd(): boolean {
    return this.off === this.len;
  }
}

/** D38: EntityId taker wire form — 255 = null. */
const takerOf = (v: number): number | null => (v === 255 ? null : v);

function lookup<T>(arr: readonly T[], idx: number): T {
  const v = arr[idx];
  if (v === undefined) throw new ProtocolError(`decodeState: enum index ${idx} out of range`);
  return v;
}
function cardOf(idx: number): CardKind | null {
  if (idx === 0) return null;
  if (idx === 1) return 'YELLOW';
  if (idx === 2) return 'RED';
  throw new ProtocolError(`decodeState: bad card index ${idx}`);
}

function readPlayer(r: Reader): PlayerState {
  const id = r.u8();
  const team = r.u8() as TeamIdx;
  const pos = { x: r.f(), y: r.f() };
  const vel = { x: r.f(), y: r.f() };
  const facing = r.f();
  const z = r.f();
  const vz = r.f();
  const stamina = r.f();
  const action = lookup(ACTIONS, r.u8());
  const actionTicks = r.i32();
  const charge = r.u8() ? { button: r.u32(), ticks: r.i32() } : null;
  const sprinting = r.u8() === 1;
  const prevButtons = r.u32();
  const shouting = r.u8() === 1;
  const calling = r.u8() === 1; // D42
  const offsideLiable = r.u8() === 1;
  const intentBits = r.u32();
  const intentTick = r.i32();
  const card = cardOf(r.u8());
  const sentOff = r.u8() === 1;
  const suspendedUntilTick = r.u32(); // D65c
  const isGk = r.u8() === 1;
  const punchHit = r.u8() === 1;
  const lastContact = r.u8()
    ? { by: r.u8(), tick: r.u32(), impulse: { x: r.f(), y: r.f() } }
    : null;
  return { id, team, pos, vel, facing, z, vz, stamina, action, actionTicks, charge, sprinting, prevButtons, shouting, calling, offsideLiable, intentBits, intentTick, card, sentOff, suspendedUntilTick, isGk, punchHit, lastContact };
}

/** Decode a canonical MatchState blob (§9.4). Strict: throws ProtocolError on any malformation. */
export function decodeState(bytes: Uint8Array): MatchState {
  const r = new Reader(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength), bytes.byteLength);
  const tick = r.u32();
  const rng = r.u32();
  const phase = lookup(PHASES, r.u8());
  const phaseTicksLeft = r.i32();
  const half = r.u8() as 1 | 2 | 3;
  const clockTicks = r.i32();
  const attackDir: [1 | -1, 1 | -1] = [r.i32() as 1 | -1, r.i32() as 1 | -1];
  const score: [number, number] = [r.u32(), r.u32()];
  const conditions = lookup(CONDITIONS, r.u8());
  const mode = {
    teamSize: r.u8() as 1 | 2 | 3 | 5 | 11,
    offside: r.u8() === 1,
    halfLengthTicks: r.u32(),
    overtime: (r.u8() === 1 ? 'GOLDEN_GOAL' : 'DRAW') as 'DRAW' | 'GOLDEN_GOAL',
    conditions: lookup(CONDITIONS, r.u8()),
  };
  const restart = r.u8()
    ? { kind: lookup(RESTARTS, r.u8()), spot: { x: r.f(), y: r.f() }, team: r.u8() as TeamIdx, taker: takerOf(r.u8()) }
    : null;
  const celebration = r.u8()
    ? (() => {
        const raw = r.u8();
        const team = r.u8() as TeamIdx;
        const n = r.u8();
        if (n > 64) throw new ProtocolError('decodeState: implausible celebrant count');
        const celebrants: number[] = [];
        for (let i = 0; i < n; i++) celebrants.push(r.u8());
        return { scorer: raw === 255 ? null : raw, team, celebrants };
      })()
    : null;
  const nPlayers = r.u32();
  if (nPlayers > 64) throw new ProtocolError('decodeState: implausible player count');
  const players: PlayerState[] = [];
  for (let i = 0; i < nPlayers; i++) players.push(readPlayer(r));
  const ball: BallState = {
    pos: { x: r.f(), y: r.f(), z: r.f() },
    vel: { x: r.f(), y: r.f(), z: r.f() },
    spin: { x: r.f(), y: r.f(), z: r.f() },
    possessor: (() => {
      const p = r.i32();
      return p < 0 ? null : p;
    })(),
    gkLock: (() => {
      const p = r.i32();
      return p < 0 ? null : p;
    })(),
    claimCooldownTicks: r.i32(),
    claimCooldownOnly: takerOf(r.u8()), // D43 (255 = null)
    kickedBy: takerOf(r.u8()), // D43 (255 = null)
    trappedTicks: r.i32(),
    shotPending: r.u8() !== 0,
    lastTouch: r.u8() ? { entity: r.u8(), team: r.u8() as TeamIdx, tick: r.u32() } : null,
  };
  const referee: RefereeState = {
    pos: { x: r.f(), y: r.f() },
    vel: { x: r.f(), y: r.f() },
    viewDir: r.f(),
    aiTargetPos: { x: r.f(), y: r.f() },
    scanTicksLeft: r.i32(),
  };
  const nDisc = r.u32();
  if (nDisc > 64) throw new ProtocolError('decodeState: implausible discipline count');
  const perPlayer: DisciplineState['perPlayer'] = [];
  for (let i = 0; i < nDisc; i++) {
    perPlayer.push({
      yellows: r.u8() as 0 | 1 | 2,
      red: r.u8() === 1,
      diveReputation: r.f(),
      foulsCommitted: r.i32(),
      foulsSuffered: r.i32(),
      punchesThrown: r.i32(),
      divesAttempted: r.i32(),
      divesWon: r.i32(),
      divesCaught: r.i32(),
    });
  }
  const advantage: AdvantageState | null = r.u8()
    ? {
        offender: r.u8(),
        victim: r.u8(),
        victimTeam: r.u8() as TeamIdx,
        severity: r.f(),
        ticksLeft: r.i32(),
        restart: { kind: lookup(RESTARTS, r.u8()), spot: { x: r.f(), y: r.f() }, team: r.u8() as TeamIdx, taker: takerOf(r.u8()) },
      }
    : null;
  const nCards = r.u32();
  if (nCards > 64) throw new ProtocolError('decodeState: implausible card-queue count');
  const cardQueue: PendingCard[] = [];
  for (let i = 0; i < nCards; i++) {
    cardQueue.push({ entity: r.u8(), card: cardOf(r.u8())!, reason: lookup(REASONS, r.u8()) });
  }
  if (!r.atEnd) throw new ProtocolError('decodeState: trailing bytes');
  return { tick, rng, phase, celebration, phaseTicksLeft, restart, half, clockTicks, attackDir, score, players, ball, referee, discipline: { perPlayer }, advantage, cardQueue, conditions, mode };
}
